import React from "react";
import {
  AlertCircle,
  CloudOff,
  ServerCrash,
  ShieldAlert,
  WifiOff,
} from "lucide-react";
import { RefreshCWIcon } from "../../components/icons";

import { API_ERROR_KIND } from "../../utils/classifyApiError";
import { automationTestIds } from "../../testing/automationTestIds";

const KIND_ICONS = {
  [API_ERROR_KIND.OFFLINE]: WifiOff,
  [API_ERROR_KIND.FORBIDDEN]: ShieldAlert,
  [API_ERROR_KIND.NOT_FOUND]: AlertCircle,
  [API_ERROR_KIND.SERVER]: ServerCrash,
  [API_ERROR_KIND.INVALID_RESPONSE]: CloudOff,
  [API_ERROR_KIND.UNKNOWN]: AlertCircle,
};

const PageErrorState = ({
  kind = API_ERROR_KIND.UNKNOWN,
  title,
  description,
  primaryLabel,
  onPrimary,
  secondaryLabel,
  onSecondary,
  details = null,
  variant = "page",
  isRetrying = false,
}) => {
  const Icon = KIND_ICONS[kind] || AlertCircle;
  const isSection = variant === "section";

  return (
    <div
      id={automationTestIds.pageErrorState}
      role="alert"
      aria-live="polite"
      className={`w-full bg-white border border-gray-200 rounded-2xl ${
        isSection ? "p-8 sm:p-10" : "p-10 sm:p-14 shadow-sm"
      }`}
    >
      <div className="mx-auto flex max-w-lg flex-col items-center text-center gap-4">
        <div
          className="flex h-14 w-14 items-center justify-center rounded-full bg-cfTableHeaderBackgroundColor text-cfPrimary"
          aria-hidden="true"
        >
          <Icon className="h-7 w-7" strokeWidth={1.75} />
        </div>

        <div className="flex flex-col gap-2">
          <h2 className="text-xl font-semibold text-cfHeadingText">{title}</h2>
          {description ? (
            <p className="text-sm leading-relaxed text-gray-500">
              {description}
            </p>
          ) : null}
        </div>

        <div className="mt-2 flex flex-col sm:flex-row items-center justify-center gap-3">
          {onPrimary && primaryLabel ? (
            <button
              type="button"
              id={automationTestIds.pageErrorStateRetry}
              onClick={onPrimary}
              disabled={isRetrying}
              className="inline-flex items-center justify-center gap-2 min-w-[9rem] px-5 py-2.5 text-sm font-medium text-white bg-cfPrimaryButton hover:bg-cfPrimaryDark rounded-[8px] disabled:bg-cfDisabled disabled:cursor-not-allowed transition-colors"
            >
              <RefreshCWIcon
                size={16}
                className={`inline-flex ${isRetrying ? "animate-spin" : ""}`}
                aria-hidden="true"
              />
              {primaryLabel}
            </button>
          ) : null}

          {onSecondary && secondaryLabel ? (
            <button
              type="button"
              id={automationTestIds.pageErrorStateSecondary}
              onClick={onSecondary}
              disabled={isRetrying}
              className="inline-flex items-center justify-center min-w-[9rem] px-5 py-2.5 text-sm font-medium text-cfHeadingText bg-white border border-gray-200 hover:bg-cfTableHeaderBackgroundColor rounded-[8px] disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
            >
              {secondaryLabel}
            </button>
          ) : null}
        </div>

        {details ? (
          <p className="mt-1 max-w-md text-xs text-gray-400 break-words">
            {details}
          </p>
        ) : null}
      </div>
    </div>
  );
};

export default PageErrorState;
